import React, { useEffect, useState } from 'react';
import { IconButton, Container, Button, Table, TableBody, TableCell, TableHead, TableRow, Paper, TextField, Pagination, FormControl, InputLabel, Select, MenuItem, Box } from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import PageLayout from '../components/PageLayout';
import CreateDocumentClassificationDialog from '../components/CreateDocumentClassificationDialog';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EditDocumentClassificationDialog from '../components/EditDocumentClassificationDialog';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';

export default function DocumentClassificationList() {
    const [classifications, setClassifications] = useState([]);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const pageSize = 10;
    const [dialogOpen, setDialogOpen] = useState(false);
    const [documentDomains, setDocumentDomains] = useState([]);
    const [selectedDomain, setSelectedDomain] = useState('');

    const [deleteError, setDeleteError] = useState('');
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);


    // Fetch document domains for the dropdown filter.
    const fetchDocumentDomains = async () => {
        try {
            const response = await fetch('/api/DocumentDomains', {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                setDocumentDomains(data);
            }
        } catch (error) {
            console.error('Error fetching document domains:', error);
        }
    };

    // Set default selected domain if none is selected.
    useEffect(() => {
        if (documentDomains.length > 0 && !selectedDomain) {
            // For example, default to the first domain in the list.
            setSelectedDomain(documentDomains[0].domainId);
        }
    }, [documentDomains, selectedDomain]);

    const fetchClassifications = async (pageNum = 1, searchValue = '', domainId = '') => {
        try {
            const domainParam = domainId ? `&documentDomainId=${domainId}` : '';
            const response = await fetch(`/api/DocumentClassifications/search?search=${encodeURIComponent(searchValue)}${domainParam}&page=${pageNum}&pageSize=${pageSize}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                setClassifications(data.classifications);
                setTotal(data.total);
            }
        } catch (error) {
            console.error('Error fetching classifications:', error);
        }
    };

    useEffect(() => {
        fetchDocumentDomains();
    }, []);

    useEffect(() => {
        fetchClassifications(page, search, selectedDomain);
    }, [page, search, selectedDomain]);

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPage(1);
    };

    const handleDomainChange = (e) => {
        setSelectedDomain(e.target.value);
        setPage(1);
    };

    const handleDialogOpen = () => {
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const handleCreateClassification = async (newClassification) => {
        try {
            const response = await fetch('/api/DocumentClassifications', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newClassification)
            });
            if (response.ok) {
                await fetchClassifications(page, search, selectedDomain);
                setDialogOpen(false);
            } else {
                const errorText = await response.text();
                alert("Error creating classification: " + errorText);
            }
        } catch (error) {
            console.error("Error creating classification:", error);
        }
    };

    const handleDeleteClick = (id) => {
        setDeleteId(id);
        setConfirmDeleteOpen(true);
    };

    const handleDeleteConfirmed = async () => {
        try {
            const response = await fetch(`/api/DocumentClassifications/${deleteId}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                fetchClassifications(page, search, selectedDomain);
            } else {
                const errorText = await response.text();
                setDeleteError(errorText);
                setErrorDialogOpen(true);
            }
        } catch (error) {
            console.error("Error deleting classification:", error);
            setDeleteError("An error occurred while deleting.");
            setErrorDialogOpen(true);
        } finally {
            setConfirmDeleteOpen(false);
            setDeleteId(null);
        }
    };



    // For deletion: ask for confirmation and then call the DELETE endpoint
    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this classification?")) {
            try {
                const response = await fetch(`/api/DocumentClassifications/${id}`, {
                    method: 'DELETE',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' }
                });
                if (response.ok) {
                    // Refresh the list after deletion
                    fetchClassifications(page, search, selectedDomain);
                } else {
                    const errorText = await response.text();
                    setDeleteError(errorText);
                    setErrorDialogOpen(true);
                }
            } catch (error) {
                console.error("Error deleting classification:", error);
                setDeleteError("An error occurred while deleting.");
                setErrorDialogOpen(true);
            }
        }
    };


    // For editing, you'll want to open an edit dialog with the current data pre-filled.
    // This is similar to the Create dialog but for updating.
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingClassification, setEditingClassification] = useState(null);

    const handleEdit = (classification) => {
        setEditingClassification(classification);
        setEditDialogOpen(true);
    };

    const handleEditDialogClose = () => {
        setEditDialogOpen(false);
        setEditingClassification(null);
    };

    const handleUpdateClassification = async (updatedClassification) => {
        try {
            const response = await fetch(`/api/DocumentClassifications/${updatedClassification.classificationId}`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedClassification)
            });
            if (response.ok) {
                // Refresh the list after updating
                fetchClassifications(page, search, selectedDomain);
                handleEditDialogClose();
            } else {
                const errorText = await response.text();
                alert("Error updating classification: " + errorText);
            }
        } catch (error) {
            console.error("Error updating classification:", error);
        }
    };

    return (
        <Container>
            <PageLayout
                breadcrumb={[
                    { label: 'Home', to: '/' },
                    { label: 'Documents', to: '/documents' },
                    { label: 'Document Classifications' }
                ]}
                showBack={false}
                showClose={false}
                icon={<CategoryIcon sx={{ fontSize: 48 }} />}
                title="Document Classifications"
                subTitle="Manage and create document classifications"
            >
                {/* Top row with search, domain dropdown, and create button */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        mb: 2,
                    }}
                >
                    <TextField
                        label="Search"
                        variant="outlined"
                        value={search}
                        onChange={handleSearchChange}
                        sx={{ flex: 1, minWidth: 200 }}
                    />
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel id="document-domain-label">Document Domain</InputLabel>
                        <Select
                            labelId="document-domain-label"
                            value={selectedDomain}
                            label="Document Domain"
                            onChange={handleDomainChange}
                        >
                            {documentDomains.map((domain) => (
                                <MenuItem key={domain.domainId} value={domain.domainId}>
                                    {domain.domainName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button variant="contained" color="primary" onClick={handleDialogOpen}>
                        Create New Classification
                    </Button>
                </Box>

                {/* Create dialog */}
                <CreateDocumentClassificationDialog
                    open={dialogOpen}
                    onClose={handleDialogClose}
                    onCreate={handleCreateClassification}
                    defaultDomain={selectedDomain}
                    documentDomains={documentDomains}
                />

                {/* Table with classifications */}
                <Paper>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Classification Name</TableCell>
                                <TableCell>Domain</TableCell>
                                <TableCell>LOS Doc</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {classifications.map((c) => (
                                <TableRow key={c.classificationId}>
                                    <TableCell>{c.classificationId}</TableCell>
                                    <TableCell>{c.classificationName}</TableCell>
                                    <TableCell>{c.documentDomainName}</TableCell>
                                    <TableCell>{c.alias || '-'}</TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => handleEdit(c)} size="small">
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton onClick={() => handleDeleteClick(c.classificationId)} size="small" color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>

                {/* Pagination */}
                <Pagination
                    count={Math.ceil(total / pageSize)}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    sx={{ mt: 2 }}
                />

                {/* Place the edit dialog here so it's rendered alongside the rest of the page */}
                {editDialogOpen && editingClassification && (
                    <EditDocumentClassificationDialog
                        open={editDialogOpen}
                        onClose={handleEditDialogClose}
                        onUpdate={handleUpdateClassification}
                        initialData={editingClassification}
                        documentDomains={documentDomains}
                    />
                )}

                <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)}>
                    <DialogTitle>Error</DialogTitle>
                    <DialogContent>
                        {deleteError}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setErrorDialogOpen(false)}>Close</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
                    <DialogTitle>Confirm Deletion</DialogTitle>
                    <DialogContent>
                        Are you sure you want to delete this classification?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
                        <Button variant="contained" color="error" onClick={handleDeleteConfirmed}>
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>


            </PageLayout>
        </Container>
    );

}
